import React, { useContext, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";

import { Context as PermissoesContext } from "../../../Context/PermissoesContext";
import { api } from "../../../services/Api";

function getMensagemErro(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.mensagem ||
    error?.response?.data?.erro ||
    fallback
  );
}

function normalizarUsername(username) {
  return username.trim().toUpperCase();
}

export default function PesquisaUsuario({
  usuarioSelecionado,
  setUsuarioSelecionado,
  enqueueSnackbar,
}) {
  const [valorBusca, setValorBusca] = useState("");
  const [erroBusca, setErroBusca] = useState("");
  const [loadingBusca, setLoadingBusca] = useState(false);
  const [openCriarUsuario, setOpenCriarUsuario] = useState(false);
  const [usernameCriacao, setUsernameCriacao] = useState("");
  const [erroCriacao, setErroCriacao] = useState("");
  const [loadingCriacao, setLoadingCriacao] = useState(false);
  const [ultimoUsernameNaoEncontrado, setUltimoUsernameNaoEncontrado] =
    useState("");
  const { checarPermissaoSemLog } = useContext(PermissoesContext);

  const podeCriarUsuario = useMemo(
    () => checarPermissaoSemLog("CA001") && checarPermissaoSemLog("CA002"),
    [checarPermissaoSemLog],
  );

  async function buscarUsuarioPorUsername(username) {
    const { data } = await api.get(
      `/v2/controleAcesso/usuario?username=${encodeURIComponent(username)}`,
    );

    return data.usuario;
  }

  async function pesquisarUsuario(e) {
    e.preventDefault();
    const username = normalizarUsername(valorBusca);

    if (!username) {
      setErroBusca("Informe um username para buscar.");
      setUsuarioSelecionado(null);
      return;
    }

    setErroBusca("");
    setUltimoUsernameNaoEncontrado("");
    setLoadingBusca(true);

    try {
      const usuario = await buscarUsuarioPorUsername(username);

      if (!usuario) {
        setErroBusca("Usuário não encontrado.");
        setUltimoUsernameNaoEncontrado(username);
        setUsuarioSelecionado(null);
        return;
      }

      setUsuarioSelecionado(usuario);
      setValorBusca(username);
      enqueueSnackbar("Permissões do usuário carregadas.", {
        variant: "success",
        autoHideDuration: 2500,
      });
    } catch (error) {
      const mensagem =
        error?.response?.status === 404
          ? getMensagemErro(error, "Usuário não encontrado.")
          : getMensagemErro(error, "Não foi possível buscar o usuário.");

      setErroBusca(mensagem);
      setUsuarioSelecionado(null);

      if (error?.response?.status === 404) {
        setUltimoUsernameNaoEncontrado(username);
      }
    } finally {
      setLoadingBusca(false);
    }
  }

  function abrirCriacao(usernameInicial = "") {
    setUsernameCriacao(normalizarUsername(usernameInicial));
    setErroCriacao("");
    setOpenCriarUsuario(true);
  }

  function fecharCriacao() {
    if (!loadingCriacao) {
      setOpenCriarUsuario(false);
      setErroCriacao("");
    }
  }

  async function criarUsuario(e) {
    e.preventDefault();
    const username = normalizarUsername(usernameCriacao);

    if (!username) {
      setErroCriacao("Informe um username para criar o usuário.");
      return;
    }

    setErroCriacao("");
    setLoadingCriacao(true);

    try {
      const { data } = await api.post(
        `/v2/controleAcesso/controleAcessoUser/${encodeURIComponent(username)}`,
      );
      const usuarioCriado = data.usuario || data;
      const usuarioSelecionavel = usuarioCriado?.id
        ? usuarioCriado
        : await buscarUsuarioPorUsername(username);

      if (!usuarioSelecionavel?.id) {
        throw new Error("Resposta de criação sem usuário válido.");
      }

      setUsuarioSelecionado(usuarioSelecionavel);
      setValorBusca(usuarioSelecionavel.username || username);
      setUltimoUsernameNaoEncontrado("");
      setOpenCriarUsuario(false);
      enqueueSnackbar("Usuário criado com sucesso.", {
        variant: "success",
        autoHideDuration: 3000,
      });
    } catch (error) {
      setErroCriacao(
        getMensagemErro(error, "Não foi possível criar o usuário."),
      );
    } finally {
      setLoadingCriacao(false);
    }
  }

  return (
    <section className="controle-acesso-search-card">
      <form className="user-search-wrapper" onSubmit={pesquisarUsuario}>
        <TextField
          className="controle-acesso-search-input"
          label="Usuário"
          size="small"
          value={valorBusca}
          onChange={(event) => {
            setValorBusca(event.target.value.toUpperCase());
            setErroBusca("");
          }}
          error={!!erroBusca}
          helperText={erroBusca || " "}
          disabled={loadingBusca}
        />

        <Button
          className="controle-acesso-action-button"
          variant="contained"
          type="submit"
          disabled={loadingBusca}
          startIcon={<SearchIcon />}
        >
          {loadingBusca ? "Buscando" : "Buscar"}
        </Button>

        {podeCriarUsuario && (
          <Button
            className="controle-acesso-action-button controle-acesso-create-button"
            variant="outlined"
            type="button"
            onClick={() =>
              abrirCriacao(ultimoUsernameNaoEncontrado || valorBusca)
            }
            startIcon={<AddIcon />}
          >
            Criar usuário
          </Button>
        )}
      </form>

      {ultimoUsernameNaoEncontrado && podeCriarUsuario && (
        <div className="controle-acesso-inline-feedback">
          <span>Esse usuário ainda não está cadastrado na base local.</span>
          <Button
            type="button"
            size="small"
            onClick={() => abrirCriacao(ultimoUsernameNaoEncontrado)}
          >
            Criar {ultimoUsernameNaoEncontrado}
          </Button>
        </div>
      )}

      {!usuarioSelecionado && (
        <div className="controle-acesso-empty-state">
          Busque um usuário ou crie um novo para gerenciar permissões.
        </div>
      )}

      <Dialog
        open={openCriarUsuario}
        onClose={fecharCriacao}
        fullWidth
        maxWidth="xs"
      >
        <form onSubmit={criarUsuario}>
          <DialogTitle>Criar usuário</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label="Username"
              size="small"
              value={usernameCriacao}
              onChange={(event) => {
                setUsernameCriacao(event.target.value.toUpperCase());
                setErroCriacao("");
              }}
              error={!!erroCriacao}
              helperText={erroCriacao || "Use o mesmo username do SoftLab."}
              disabled={loadingCriacao}
            />
          </DialogContent>
          <DialogActions>
            <Button
              type="button"
              onClick={fecharCriacao}
              disabled={loadingCriacao}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={loadingCriacao}>
              {loadingCriacao ? "Criando" : "Criar usuário"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </section>
  );
}
